import { useEffect, useRef, useState } from "react";

/**
 * useWakeLock — mantiene lo schermo acceso quando il driver è in tragitto.
 * Phase 42 — supporto best-effort. Su iOS Safari < 16.4 / browser senza Wake Lock API
 * la lib ritorna `supported=false` e l'integrazione si comporta come no-op.
 *
 * @param {boolean} active — quando true, richiede il wake lock
 * @returns { supported, locked, error }
 */
export function useWakeLock(active) {
  const [supported] = useState(
    () => typeof navigator !== "undefined" && "wakeLock" in navigator && typeof navigator.wakeLock?.request === "function"
  );
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!supported || !active) {
      // Release if previously held
      if (sentinelRef.current) {
        try { sentinelRef.current.release(); } catch { /* ignore */ }
        sentinelRef.current = null;
      }
      setLocked(false);
      return;
    }

    let cancelled = false;
    const request = async () => {
      try {
        const sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try { sentinel.release(); } catch { /* ignore */ }
          return;
        }
        sentinelRef.current = sentinel;
        setLocked(true);
        setError(null);
        sentinel.addEventListener("release", () => {
          if (!cancelled) setLocked(false);
        });
      } catch (e) {
        setError(e?.message || "Wake lock failed");
        setLocked(false);
      }
    };
    request();

    // Re-acquire on tab visibility change (browser releases it when tab is hidden)
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !sentinelRef.current) {
        request();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (sentinelRef.current) {
        try { sentinelRef.current.release(); } catch { /* ignore */ }
        sentinelRef.current = null;
      }
      setLocked(false);
    };
  }, [supported, active]);

  return { supported, locked, error };
}
